import { useCallback, useEffect, useState } from 'react'

/** The "+ Add projection" button — the last-resort focus target. */
export const ADD_PROJECTION_BUTTON_ID = 'manage-projections-add'

/** The DataTable select-all checkbox (`selection.selectAllId`) — where focus goes when the bulk bar closes. */
export const SELECT_ALL_CHECKBOX_ID = 'manage-projections-select-all'

export type RowControl = 'date' | 'apply' | 'discard' | 'edit' | 'copy' | 'next-day' | 'delete'

/** `rowKey` is the projection id or the new-row key. */
export function rowControlId(rowKey: string, control: RowControl): string {
  return `${rowKey}-${control}`
}

/** The same list saas-ui Modal traps Tab with. */
const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** Focuses the first of `ids` that exists and is enabled; true when one was. */
function focusFirst(ids: readonly string[]): boolean {
  for (const id of ids) {
    const element = document.getElementById(id)
    if (element && !element.matches(':disabled')) {
      element.focus()
      return true
    }
  }
  return false
}

/**
 * Returns `requestFocus(id)` for a state change that removes the focused control
 * (which would drop focus to `body`, outside the Modal's Tab trap): after the next
 * commit `id` gets focus, else the Add button. State, not a ref, so it lands with
 * the dispatch it is made with and a query re-render cannot consume it early.
 */
export function useFocusRequest() {
  const [request, setRequest] = useState<{ ids: string[] } | null>(null)
  useEffect(() => {
    if (request) focusFirst(request.ids)
  }, [request])
  return useCallback((id: string) => setRequest({ ids: [id, ADD_PROJECTION_BUTTON_ID] }), [])
}

/**
 * Ref callback that moves focus into the dialog (Add button, else first focusable)
 * unless it is already there. saas-ui Modal portals its content one render after
 * its single focus step, so a Modal mounted open would leave focus on the opener.
 */
export function focusIntoDialog(element: HTMLElement | null): void {
  const dialog = element?.closest('[role="dialog"]')
  if (!dialog || dialog.contains(document.activeElement)) return
  if (focusFirst([ADD_PROJECTION_BUTTON_ID])) return
  dialog.querySelector<HTMLElement>(FOCUSABLE)?.focus()
}

/**
 * Whether focus is inside the DataTable bulk actions bar (its Clear selection
 * button), which unmounts when the selection empties. Matches the saas-ui
 * `BulkActionsBar` markup: `role="toolbar"` named "Bulk actions".
 */
export function isFocusInBulkBar(): boolean {
  return document.activeElement?.closest('[role="toolbar"][aria-label="Bulk actions"]') != null
}

/**
 * Whether a Radix popper (Select list, DateField calendar) is open, so the same
 * Escape does not also close the modal. Tooltips are excluded: one lingers after
 * the pointer leaves and would swallow a Close click.
 */
export function isPopperOpen(): boolean {
  return Array.from(document.querySelectorAll('[data-radix-popper-content-wrapper]')).some(
    (wrapper) => !wrapper.querySelector('[role="tooltip"]'),
  )
}
